import { call, put, select, takeLatest } from 'redux-saga/effects'
import { toast } from 'react-toastify'

import { addPaymentOnOrder, updatePaymentOnOrder, orderDetailsRequested, addTenderOnOrder, mutationRequested, mutationSucceeded, mutationFailed } from '../../actions/order'

import MutationApi from '../../remote/mutationApi'
import Log from '../../utils/Log'
import { getOrder } from '../../selectors/order'
import PaymentTypes from '../../utils/paymentTypes'
import { getUserNameFromCard, makeUserAttributes } from '../../utils/orderUtils'
import { customHistory } from '../../stores'

const customerPaymentData = (paymentPreference) => {
  const customerPaymentId = paymentPreference.uuid
  const paymentType = PaymentTypes.nameFromInteger(paymentPreference.paymentType) || 'cash'

  let paymentDisplay = paymentPreference.shortDescription

  if (PaymentTypes.isManualCc(paymentType)) paymentDisplay = paymentDisplay ?? 'Manual CC'

  if (PaymentTypes.isDirectBill(paymentType)) paymentDisplay = 'Direct Bill'
  if (PaymentTypes.isHouseAccount(paymentType)) paymentDisplay = 'House Account'
  if (PaymentTypes.isFnbCredit(paymentType)) paymentDisplay = 'F&B Credit'

  return { customerPaymentId, paymentDisplay, paymentType }
}

const directTo = (method = 'push', path, state) => {
  if (path === undefined) return

  customHistory[method](path, state)
}

function* addPaymentOnOrderSaga(params = {}) {
  const payload = params?.payload
  const orderId = payload?.orderId
  const order = payload?.order
  const isCash = payload?.payments?.[0]?.paymentType === 'cash'

  try {
    yield put(mutationRequested({ orderId, mutationType: 'addPayment' }))
    const result = yield call(MutationApi.addPayment, payload)
    const applied = result?.data?.orderMutations?.[0]?.applied

    if (applied) {
      yield put(orderDetailsRequested(orderId))
      yield put(mutationSucceeded({ orderId }))
      if (isCash) {
        yield call(directTo, 'push', `/receipt/${orderId}/cash-receipt`, { redirectTo: `/tabs/${orderId}` })
      }
      if(!order?.signatureRequired && order?.isNoTipMenu) {
        yield call(directTo, 'push', `/receipt/${orderId}`)
      }
    } else {
      yield put(mutationFailed({ orderId }))
      yield call(directTo, 'push', `/tabs/${orderId}`)
      Log.error('Add payment to order Failed', { orderId, payload })
    }
  } catch (err) {
    yield put(mutationFailed({ orderId }))
    yield call(directTo, 'push', `/tabs/${orderId}`)
    Log.error('Add payment to order Failed', { orderId, payload })
  }
}

export function* watchAddPayment() {
  yield takeLatest(addPaymentOnOrder.type, addPaymentOnOrderSaga)
}

function* updatePaymentOnOrderSaga(params = {}) {
  const payload = params?.payload
  const orderId = payload?.orderId ?? ''
  const paymentId = payload?.paymentUuid ?? ''
  const receiptRouteParams = [directTo, 'push', `/receipt/${orderId}/${paymentId}`, { redirectTo: paymentId ? `/tabs/${orderId}` : '/tabs' }]

  try {
    yield put(mutationRequested({ orderId, mutationType: 'updatePayment' }))
    const result = yield call(MutationApi.updatePayment, payload)
    const applied = result?.data?.orderMutations?.[0]?.applied
    if (applied) {
      yield put(orderDetailsRequested(orderId))
      yield put(mutationSucceeded({ orderId }))
      yield call(...receiptRouteParams)
    } else {
      yield put(mutationFailed({ orderId }))
      Log.error('Update payment to order Failed', { orderId, payload })
      yield call(...receiptRouteParams)
    }
  } catch (err) {
    yield put(mutationFailed({ orderId }))
    Log.error('Update payment to order Failed', { orderId, payload })
    yield call(...receiptRouteParams)
  }
}

export function* watchUpdatePayment() {
  yield takeLatest(updatePaymentOnOrder.type, updatePaymentOnOrderSaga)
}

function* addTenderOnOrderSaga(params = {}) {
  const orderId = params?.payload?.orderId
  const cardReaderData = params?.payload?.cardReaderData || {}
  const customTender = params?.payload?.customTender
  const amountInCents = params?.payload?.amountInCents ?? 0
  const useStoredCard = params?.payload?.useStoredCard
  const useNewCard = params?.payload?.useNewCard
  const useCash = params?.payload?.useCash
  const useExistingTender = params?.payload?.useExistingTender
  const order = yield select(getOrder, orderId)

  let userAttributes
  let userName
  let payload
  let shortDescription

  if (useExistingTender) {
    userAttributes = params.payload.payment?.userAttributes
    shortDescription = params.payload.payment?.shortDescription
  }

  if (useStoredCard) {
    userAttributes = order?.userAttributes
    userName = order?.userName
  }

  if (useNewCard) {
    userAttributes = makeUserAttributes(cardReaderData)
    userName = getUserNameFromCard(cardReaderData)
  }

  if (userAttributes) {
    // FIXME @westinschepper: This is a temporary solution.
    shortDescription = shortDescription ?? `${userName} / ${cardReaderData.cardType} ${userAttributes.last_4}`
    const payments = [{ amountInCents, shortDescription, paymentType: useExistingTender ? params.payload.payment?.paymentType : 'freedompay_credit', userAttributes }]
    payload = { orderId, payments, userAttributes, userName }
  }

  if (customTender) {
    const payments = [{ amountInCents, paymentType: customTender.name, shortDescription: customTender.displayName }]
    payload = { orderId, payments }
  }

  if (useCash) {
    const payments = [{ amountInCents, shortDescription: 'Cash', paymentType: 'cash' }]
    payload = { orderId, payments }
  }

  yield put(addPaymentOnOrder(payload))
}

export function* watchAddTender() {
  yield takeLatest(addTenderOnOrder.type, addTenderOnOrderSaga)
}

export function* updatePaymentOnOrderRichCheckoutSaga(params = {}) {
  const payload = params?.payload
  const orderId = payload?.orderId
  const { payments, ...restPayload } = payload

  const newPayload = {
    ...restPayload,
    payments: [{ ...payments, userAttributes: payload.userAttributes }]
  }

  try {
    const result = yield call(MutationApi.addPayment, newPayload)
    const applied = result?.data?.orderMutations?.[0]?.applied
    if (applied) {
      yield put(orderDetailsRequested(orderId))
    }
  } catch (err) {
    const errorMessage = err?.response?.data?.errorMessage ?? "Sorry, but something went wrong"
    Log.error('Add payment to order Failed', { errorMessage, orderId, payload })
  }
}
